import * as R from "ramda";

import { arrayOf, bool, number, shape, string } from "prop-types";
import {
  createFacetString,
  createPageString,
  initFacetsFromQs,
  updateFacets,
  updateQueryParams,
  updateResponse,
} from "../../common/helpers.js";
import { useEffect, useRef, useState } from "react";

import CallToAction from "../CallToAction/CallToAction.jsx";
import { FacetBubbles } from "./FacetBubbles.jsx";
import Facets from "./Facets.jsx";
import Loader from "../Loader/Loader.jsx";
import VariantOne from "./VariantOne.jsx";
import VariantThree from "./VariantThree.jsx";
import VariantTwo from "./VariantTwo.jsx";
import VariantFive from "./VariantFive.jsx";
import {
  mockApiResponse,
  mockApiResponseNoImages,
  mockApiResponseVariantFive,
  mockApiResponseVariantTwo,
  mockApiResponseVariantThree,
} from "./mockData.js";
import cx from "classnames";

const propTypes = {
  storybook: bool,
  index: number,
  dataModel: shape({
    url: string.isRequired,
    loadMore: bool.isRequired,
    loadMoreText: string.isRequired,
    perPage: number.isRequired,
    title: string,
    facetKeys: arrayOf(string),
    showFacetsLabel: string.isRequired,
    cta: shape({ href: string, text: string, target: string }),
  }).isRequired,
  dataQuery: shape({
    pageId: string.isRequired,
    listingId: string.isRequired,
    showSummaries: bool,
    showReadTimes: bool,
    showEyebrow: bool,
    showImages: bool,
    variantType: number,
  }).isRequired,
};

const DynamicContentListing = (props) => {
  const {
    storybook,
    index,
    dataModel: {
      url,
      loadMore,
      loadMoreText,
      title,
      cta,
      facetKeys,
      showFacetsLabel,
    },
    dataQuery: {
      pageId,
      listingId,
      showSummaries,
      showReadTimes,
      showEyebrow,
      showImages,
      variantType,
    },
  } = props;

  const [response, setResponse] = useState({});

  const [activeFacets, setActiveFacets] = useState({
    ...initFacetsFromQs(facetKeys),
    page: 1,
  });

  const [facetMenuOpen, setFacetMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const facetStringRef = useRef();

  const hideLoadMore =
    Object.keys(response).length > 0 &&
    response.results.length === response.totalResults;

  useEffect(() => {
    setIsLoading(true);
    const facetString = createFacetString(activeFacets);
    const pageString = createPageString(activeFacets);

    if (storybook === true) {
      // Pull the results from our mock API data
      const apiResponse =
        variantType === 5
          ? mockApiResponseVariantFive
          : variantType === 3
          ? mockApiResponseVariantThree
          : variantType === 2
          ? mockApiResponseVariantTwo
          : showImages === false
          ? mockApiResponseNoImages
          : mockApiResponse;

      setResponse((prevState) =>
        R.mergeDeepWithKey(
          (k, l, r) => (k === "results" ? R.concat(l, r) : r),
          prevState,
          apiResponse,
        ),
      );
      facetStringRef.current = facetString;
      updateQueryParams(facetKeys, activeFacets);
      setIsLoading(false);
    } else {
      // Fetch the results from the API
      fetch(
        `${url}?listingId=${listingId}&pageId=${pageId}&showEyebrow=${showEyebrow}&showSummaries=${showSummaries}&showReadTimes=${showReadTimes}&showImages=${showImages}${
          facetString ? facetString : ""
        }&${pageString}&variantType=${variantType}`,
      )
        .then((response) => response.json())
        .then((data) =>
          setResponse((prevState) =>
            updateResponse(facetString, facetStringRef, data, prevState),
          ),
        )
        .catch((e) => console.warn(e))
        .finally(() => {
          facetStringRef.current = facetString;
          updateQueryParams(facetKeys, activeFacets);
          setIsLoading(false);
        });
    }
  }, [
    url,
    listingId,
    pageId,
    showEyebrow,
    showSummaries,
    showReadTimes,
    loadMore,
    showImages,
    activeFacets,
    facetKeys,
    variantType,
    storybook,
  ]);

  const handlePageChange = (page) =>
    setActiveFacets((prev) => R.evolve({ ["page"]: () => page }, prev));

  const handleFacetClick = (facetId, valueId) =>
    setActiveFacets((prev) => {
      return updateFacets(facetId, valueId, prev);
    });

  const clearAllFacets = () => {
    setActiveFacets({ page: 1 });
  };

  return (
    <section className="dynamic-content-listing">
      <div className="container--100">
        {title && (
          <div className="container--100">
            <h2 className="dynamic-content-listing__title h2">{title}</h2>
          </div>
        )}
        <div className="dynamic-content-listing__facets">
          {response?.facets && (
            <Facets
              facets={response.facets}
              facetMenuOpen={facetMenuOpen}
              setFacetMenuOpen={setFacetMenuOpen}
              onFacetClick={handleFacetClick}
              showFacetsLabel={showFacetsLabel}
            />
          )}
        </div>

        <FacetBubbles
          clearAllFacets={clearAllFacets}
          onFacetClick={handleFacetClick}
          totalResults={response?.totalResults}
          setFacetMenuOpen={setFacetMenuOpen}
          facetMenuOpen={facetMenuOpen}
          activeFacets={R.compose(
            R.when(
              (obj) => Object.keys(obj).length === 0,
              () => null,
            ),
            R.pickBy((val, key) => key !== "page"),
          )(activeFacets)}
        />

        <div className="container--100 dynamic-content-listing__results">
          {variantType === 1 && <VariantOne results={response?.results} />}
          {variantType === 2 && <VariantTwo results={response?.results} />}
          {variantType === 3 && <VariantThree results={response?.results} />}
          {variantType === 5 && (
            <VariantFive results={response?.results} index={index} />
          )}
        </div>

        {isLoading && <Loader />}

        {(cta || loadMore) && (
          <div
            className={cx("container dynamic-content-listing__cta", {
              "dynamic-content-listing__cta--variant-one": variantType === 1,
              "dynamic-content-listing__cta--variant-three": variantType === 3,
              "dynamic-content-listing__cta--variant-five": variantType === 5,
            })}
          >
            {cta && !loadMore && (
              <CallToAction
                className="call-to-action--outline"
                href={cta?.href}
                target={cta?.target}
              >
                {cta.text}
              </CallToAction>
            )}

            {loadMore && !hideLoadMore && !cta && (
              <button
                className="call-to-action call-to-action--outline"
                onClick={() => {
                  handlePageChange(activeFacets.page + 1);
                }}
                type="button"
              >
                {loadMoreText}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

// Specifies the default values for props:
DynamicContentListing.defaultProps = {
  storybook: false,
};

DynamicContentListing.propTypes = propTypes;
export default DynamicContentListing;
